import { ActionEvents } from '@griply/types';
import {
  useExternalCalendarConnections,
  useTwoWaySyncAllowed,
} from 'features/external-connections';
import React, { useCallback, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { ConnectCalendarCard as ConnectCalendarCardUI } from 'shared/components/ui/connect-calendar-card';
import {
  SettingsOpenOrigin,
  useOpenSettings,
} from 'shared/contexts/user-settings';
import { SettingsSection } from 'shared/dialogs/user-settings/settings-nav';
import { useDismissibleStorage } from 'shared/hooks/use-dismissible-storage';
import { useTrackEvents } from 'shared/hooks/use-track-events';
import { useUser } from 'shared/hooks/use-user';

const STORAGE_KEY_PREFIX = 'griply.connect-calendar-card.';

export const ConnectCalendarCard: React.FC = () => {
  const { t } = useTranslation();
  const user = useUser();
  const openSettings = useOpenSettings();
  const connections = useExternalCalendarConnections();
  const { allowed: twoWaySyncAllowed } = useTwoWaySyncAllowed();
  const { dismissed, dismiss } = useDismissibleStorage(
    `${STORAGE_KEY_PREFIX}${user?.uid ?? ''}`,
  );
  const track = useTrackEvents();
  const hasTrackedShown = useRef(false);
  const shown = !connections.length && !dismissed;

  // Connect event fires at the provider pick in calendar settings; this only opens the dialog.
  const handleConnect = useCallback(
    () =>
      openSettings(
        SettingsSection.Calendar,
        SettingsOpenOrigin.ConnectCalendarCard,
      ),
    [openSettings],
  );

  const handleDismiss = useCallback(() => {
    track(ActionEvents.ConnectCalendarCardDismissed, undefined);
    dismiss();
  }, [track, dismiss]);

  useEffect(() => {
    if (!shown || hasTrackedShown.current) {
      return;
    }
    hasTrackedShown.current = true;
    track(ActionEvents.ConnectCalendarCardShown, undefined);
  }, [shown, track]);

  if (!shown) {
    return null;
  }

  const subtitleKey = twoWaySyncAllowed
    ? 'today.calendar-connect-card.subtitle.premium'
    : 'today.calendar-connect-card.subtitle.free';

  return (
    <ConnectCalendarCardUI
      title={t('today.calendar-connect-card.title')}
      subtitle={t(subtitleKey)}
      connectLabel={t('today.calendar-connect-card.connect')}
      dismissLabel={t('today.calendar-connect-card.dismiss')}
      onConnect={handleConnect}
      onDismiss={handleDismiss}
    />
  );
};
